
import {
    AbcOutlined,
    CheckCircleRounded,
    CheckOutlined,
    CloseOutlined,
    DeleteForeverOutlined,
    DoNotDisturbOnRounded,
    DriveFileRenameOutlineOutlined,
    FenceOutlined,
    KeyOutlined,
    RemoveCircleOutlineOutlined,
    SaveOutlined,
} from "@mui/icons-material";

import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";

import { GridColDef } from "@mui/x-data-grid";

import {
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    AreaType,
    DepartmentSimpleType,
    GeofenceRequestType,
    GeofenceType,
    SecretarySimpleType,
    SelectedParametersType,
    UserType,
    VehicleGroupType,
} from "../../../types/types";

import { ContentProps, Toast } from "primereact/toast";
import { SetterOrUpdater, useRecoilValue } from "recoil";
import { useCookies } from "react-cookie";

import {
    getFilteredList,
    setToastRefToOpen,
} from "../../../functions/functions";

import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";

import {
    MapContainer,
    TileLayer,
    Circle,
    Polygon,
    Polyline,
    FeatureGroup
} from "react-leaflet";

import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import { EditControl } from "react-leaflet-draw";
import type { Map as LeafletMap } from "leaflet";
import { useLocation } from "react-router-dom";

import drawLocales from "leaflet-draw-locales";
import saveGeofenceByCustomerAndDepartment from "../../../resources/Api/geofence/saveGeofenceByCustomerAndDepartment";
import updateGeofenceByCustomerAndDepartment from "../../../resources/Api/geofence/updateGeofenceByCustomerAndDepartment";
import findAllGeofencesByCustomerIdAndDepartmentIdCrudResponse from "../../../resources/Api/geofence/findAllGeofencesByCustomerIdAndDepartmentIdCrudResponse";
import deleteGeofenceByIdAndCustomerAndDepartment from "../../../resources/Api/geofence/deleteGeofenceByIdAndCustomerAndDepartment";
import updateGeofenceStatusById from "../../../resources/Api/geofence/updateGeofenceStatusById";
import findAllGeofencesExportResponse from "../../../resources/Api/geofence/findAllGeofencesExportResponse";
import findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdListResponse from "../../../resources/Api/group/findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdListResponse";
import { userAtom } from "../../../atoms/atoms";
import { departmentDefault, secretaryDefault } from "../../../consts/consts";
import userFindAllSecretariesListResponse from "../../../resources/Api/user/userFindAllSecretariesListResponse";
import userFindAllDepartmentsListResponse from "../../../resources/Api/user/userFindAllDepartmentsListResponse";
import findAllPlacesWithIqLocation from "../../../resources/Api/location/findAllPlacesWithIqLocation";
import { isImpersonationLocked } from "../../../utils/impersonation";

type AddressResult = {
    display_name: string;
    lat: string | number;
    lon: string | number;
};

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={
                    <KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "name",
        align: "left",
        width: 200,
        editable: false,
        sortable: true,
        renderHeader: (item) => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Nome"}
                icon={
                    <FenceOutlined
                        style={{ height: 30, width: 30, fill: "#ffff" }}
                    />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.name}
            />
        ),
    },
    {
        field: "description",
        headerClassName: "last-column",
        cellClassName: "last-column",
        align: "left",
        editable: false,
        sortable: true,
        renderHeader: (item) => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Descrição"}
                icon={
                    <AbcOutlined
                        style={{ height: 30, width: 30, fill: "#ffff" }}
                    />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.description}
            />
        ),
    },
];

const rowsOptions = (
    list: Array<GeofenceType>,
    openDetails?: SetterOrUpdater<boolean>
): Array<GeofenceType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, openDetails }));
};

/**
 * GeofencePage - Screen that renders a page to create and manage virtual geofences
 * @component-screen
 */
const GeofencePage = ({
    isHiddenMenu,
}: {
    isHiddenMenu: boolean
}) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState(true);
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<GeofenceType>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // states
    const [geofencesList, setGeofenceList] = useState<Array<GeofenceType>>([]);
    const [selectedGeofence, setSelectedGeofence] = useState<GeofenceType>({
        name: "",
        area: "",
        customerId: -1,
        departmentId: -1,
        id: -1,
        activated: true,
    });

    const [groupsList, setGroupsList] = useState<Array<VehicleGroupType>>([]);
    const [geofenceName, setGeofenceName] = useState<string>("");
    const [geofenceDescription, setGeofenceDescription] = useState<string>("");
    const [geofenceGroupName, setGeofenceGroupName] = useState<string>("");
    const [geofenceArea, setGeofenceArea] = useState<string>("");
    const [addressQuery, setAddressQuery] = useState("");
    const [addressResults, setAddressResults] = useState<AddressResult[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(null);
    const [isSearchingAddress, setIsSearchingAddress] = useState(false);

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    const [secretariesList, setSecretariesList] = useState<Array<SecretarySimpleType>>([]);
    const [departmentsList, setDepartmentsList] = useState<Array<DepartmentSimpleType>>([]);

    const [secretary, setSecretary] = useState<SecretarySimpleType>(secretaryDefault);
    const [department, setDepartment] = useState<DepartmentSimpleType>(departmentDefault);

    // refs
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);
    const geofenceMapRef = useRef<LeafletMap | null>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks and consts
    const location = useLocation();

    const geofenceColor = useMemo(
        () => groupsList.find((item) => item.name === geofenceGroupName)?.color,
        [geofenceGroupName, groupsList]
    );

    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);

    // on load
    useEffect(() => {
        const locale = drawLocales("pt-pt");
        // draw options
        locale.draw.toolbar.buttons.polygon = "Desenhar tipo poligono";
        locale.draw.toolbar.buttons.polyline = "Desenhar tipo polilinha";
        locale.draw.toolbar.buttons.circle = "Desenhar tipo circulo";

        // draw actions
        locale.draw.toolbar.finish.text = "Finalizar";
        locale.draw.toolbar.undo.text = "Desfazer";
        locale.draw.toolbar.actions.text = "Cancelar";

        // draw edit
        locale.edit.toolbar.buttons.edit = "Editar";
        locale.edit.toolbar.buttons.remove = "Remover";

        // draw edit actions
        locale.edit.toolbar.actions.save.text = "Finalizar";
        locale.edit.toolbar.actions.cancel.text = "Cancelar";
        locale.edit.toolbar.actions.clearAll.text = "Remover tudo";

        // draw edit actions disabled
        locale.edit.toolbar.buttons.editDisabled = "Nenhuma cerca para editar";
        locale.edit.toolbar.buttons.removeDisabled = "Nenhuma cerca para remover";
    }, []);

    const handleClose = useCallback(() => {
        setLoading(false);
        setGeofenceArea("");
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setGeofenceName("");
        setGeofenceGroupName("");
        setGroupsList([]);
        setGeofenceDescription("");
        setAddressQuery("");
        setAddressResults([]);
        setSelectedAddress(null);
        setIsSearchingAddress(false);
        setIsOpenDetails(false);
        setSelectedGeofence({
            name: "",
            area: "",
            customerId: -1,
            departmentId: -1,
            id: -1,
            activated: true,
        });
        setInEdit(false);
        setInInsert(false);
        setSecretary(secretaryDefault);
        setDepartment(departmentDefault);
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    useEffect(() => {
        setAddressResults(selectedAddress ? [selectedAddress] : []);
        setIsSearchingAddress(false);
        if (!isOpenDetails || selectedAddress || addressQuery.trim().length < 3) return;

        let active = true;
        const timer = setTimeout(async () => {
            setIsSearchingAddress(true);
            try {
                const { data } = await findAllPlacesWithIqLocation(addressQuery.trim());
                if (!active) return;
                setAddressResults(Array.isArray(data) ? data.filter((place: AddressResult) =>
                    place && typeof place.display_name === "string" &&
                    String(place.lat ?? "").trim() !== "" && String(place.lon ?? "").trim() !== "" &&
                    Number.isFinite(Number(place.lat)) && Math.abs(Number(place.lat)) <= 90 &&
                    Number.isFinite(Number(place.lon)) && Math.abs(Number(place.lon)) <= 180
                ) : []);
            } catch {
                if (active) {
                    setTextValidation("Não foi possível pesquisar o endereço. Tente novamente.");
                    setStatusMessage("error");
                    setFieldsIsValid(false);
                }
            } finally {
                if (active) setIsSearchingAddress(false);
            }
        }, 500);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [addressQuery, selectedAddress, isOpenDetails]);

    const getSecretariesList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;
        try {
            const response = await userFindAllSecretariesListResponse(
                token,
                userId
            );
            setSecretariesList(response.data);
        } catch (error) {
            console.log(error);
            setSecretariesList([]);
        }
    }, [cookies.token, getCurrentUser.id]);

    useEffect(() => {
        if (currentParameters?.isViewingAll) {
            getSecretariesList();
        }
    }, [getSecretariesList, currentParameters?.isViewingAll]);

    const getDepartmentsList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;

        try {
            const response =
                await userFindAllDepartmentsListResponse(
                    token,
                    userId,
                    secretary?.id
                );

            if (response.data.length > 0) {
                const selectedDepartment: DepartmentSimpleType = response.data.find(
                    (it: any) => it?.id === selectedGeofence?.departmentId
                )!!;

                setDepartment(selectedDepartment);
                setDepartmentsList(response.data);
            }
        } catch (error) {
            console.log(error);
            setDepartmentsList([]);
        }
    }, [cookies.token, getCurrentUser.id, secretary.id, selectedGeofence?.departmentId]);

    useEffect(() => {
        if (secretary?.name) {
            getDepartmentsList();
        }
    }, [getDepartmentsList, secretary?.name]);

    const getAllGeofences = useCallback(async (search: string = "", status: string = "activated", page: number = 0, size: number = 100) => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        setLoading(true);
        try {
            const { data } =
                await findAllGeofencesByCustomerIdAndDepartmentIdCrudResponse(
                    token,
                    getCurrentUser.id,
                    currentParameters.customer.id,
                    currentParameters.department.id,
                    status,
                    page,
                    size,
                    currentParameters.isViewingAll
                );
            setGeofenceList(data.content);
            setIsLastPage(data.last);
            setNumberOfElementsPerPage(data.numberOfElements);
            setPageNumber(data.pageable.pageNumber);

            if (data.first) {
                setNumberOfElements(data.totalElements);
            }
        } catch (error: any) {
            console.log(error);
            setGeofenceList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.selectedParameters, cookies.token, getCurrentUser.id]);

    useEffect(() => {
        getAllGeofences();
    }, [getAllGeofences]);

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        // const currentParameters: SelectedParametersType = cookies.selectedParameters;
        const departmentId = currentParameters?.isViewingAll ? department?.id : currentParameters.department.id;

        setLoading(true);
        const data: GeofenceRequestType = {
            id: -1,
            name: geofenceName,
            description: geofenceDescription,
            area: geofenceArea,
            groupId: groupsList.find((item) => item.name === geofenceGroupName)?.id!!,
            customerId: currentParameters.customer.id,
            departmentId: departmentId,
        };

        if (
            !data.name ||
            !data.description ||
            !data.area ||
            !data.groupId
        ) {
            setTextValidation(
                "Todos os campos marcados com * são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await saveGeofenceByCustomerAndDepartment(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllGeofences();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        currentParameters.customer.id,
        department?.id,
        currentParameters?.isViewingAll,
        currentParameters.department.id,
        cookies.token,
        geofenceName,
        geofenceDescription,
        geofenceArea,
        geofenceGroupName,
        groupsList,
        handleClose,
        getAllGeofences,
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        setLoading(true);
        const data: GeofenceRequestType = {
            id: selectedGeofence.id,
            name: geofenceName,
            description: geofenceDescription,
            area: geofenceArea,
            groupId: groupsList.find((item) => item.name === geofenceGroupName)?.id!!,
            customerId: selectedGeofence.customerId,
            departmentId: selectedGeofence.departmentId,
        };

        if (
            !data.name ||
            !data.description ||
            !data.area ||
            !data.groupId
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.name === selectedGeofence.name &&
            data.description === selectedGeofence.description &&
            data.area === selectedGeofence.area &&
            data.groupId === selectedGeofence.groupId
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await updateGeofenceByCustomerAndDepartment(
                token,
                data.id,
                data
            );
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllGeofences();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        selectedGeofence.area,
        selectedGeofence.customerId,
        selectedGeofence.departmentId,
        selectedGeofence.description,
        selectedGeofence.id,
        selectedGeofence.name,
        selectedGeofence.groupId,
        cookies.token,
        geofenceName,
        geofenceDescription,
        geofenceArea,
        geofenceGroupName,
        groupsList,
        handleClose,
        getAllGeofences,
    ]);

    const getAllGroupsList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        // const currentParameters: SelectedParametersType = cookies.selectedParameters;
        const departmentId = (currentParameters?.department?.id == -1) ? department?.id : currentParameters?.department?.id;

        setLoading(true);
        try {
            const { data } =
                await findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdListResponse(
                    token,
                    currentParameters.customer.id,
                    currentParameters.userId,
                    (inInsert ? departmentId : selectedGeofence.departmentId),
                    "activated"
                );
            if (data.length > 0) {
                setGroupsList(data);

                setGeofenceGroupName(
                    data.find((item: any) => item.id === selectedGeofence.groupId)?.name!! ?? "");
            }
        } catch (error: any) {
            console.log(error);
            setGroupsList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.token, selectedGeofence.groupId, selectedGeofence.departmentId, department?.id, inInsert, currentParameters.customer.id, currentParameters.userId, currentParameters.department.id]);

    useEffect(() => {
        getAllGroupsList();
    }, [getAllGroupsList]);

    const onDelete = useCallback(async (id: number) => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);
        try {
            await deleteGeofenceByIdAndCustomerAndDepartment(
                token,
                currentParameters.customer.id,
                currentParameters.department.id,
                id
            );
            setTextValidation("Registro excluído com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                getAllGeofences();
            }, 3000);
        }
    }, [cookies.token, cookies.selectedParameters, getAllGeofences, handleClose]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const currentParameters: SelectedParametersType = cookies.selectedParameters;
            setLoading(true);
            try {
                await updateGeofenceStatusById(
                    token,
                    id,
                    currentParameters.customer.id,
                    status
                );
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllGeofences();
                }, 8000);
            }
        },
        [cookies.token, cookies.selectedParameters, getAllGeofences, handleClose]
    );

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const verifyBeforeDelete = useCallback(
        (id: number) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "O registro será excluído permanentemente. Deseja realmente excluir?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onDelete(id);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onDelete, isToastVisible, statusMessage]
    );

    const verifyBeforeCancel = useCallback(
        (selected: GeofenceType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                if (
                    geofenceName !== selected.name ||
                    geofenceDescription !== selected.description ||
                    geofenceArea !== selected.area ||
                    geofenceGroupName !==
                    groupsList.find((item) => item.id === selected.groupId)?.name
                ) {
                    canCancel = false;
                }
            } else if (
                !inEdit &&
                inInsert &&
                (geofenceName ||
                    geofenceDescription ||
                    geofenceArea ||
                    geofenceGroupName)
            ) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            isToastVisible,
            geofenceName,
            geofenceDescription,
            geofenceArea,
            geofenceGroupName,
            groupsList,
        ]
    );

    const onCreateFence = useCallback((e: any) => {
        const { layerType, layer } = e;
        if (layerType === "polygon") {
            const area = JSON.stringify({
                coordinates: layer.getLatLngs()[0],
                type: layerType,
            } as AreaType);
            setGeofenceArea(area);
        }

        if (layerType === "circle") {
            const area = JSON.stringify({
                coordinates: layer.getLatLng(),
                type: layerType,
                radius: layer.getRadius(),
            } as AreaType);
            setGeofenceArea(area);
        }

        if (layerType === "polyline") {
            const area = JSON.stringify({
                coordinates: layer.getLatLngs(),
                type: layerType,
            } as AreaType);
            setGeofenceArea(area);
        }
    }, []);

    const onEditFence = useCallback((e: any) => {
        const newFence: any = Object.values(e.layers._layers);
        const options = newFence[0]?.options;
        if (options) {
            const type =
                options.fill && options?.radius
                    ? "circle"
                    : options.fill && !options?.radius
                        ? "polygon"
                        : "polyline";

            if (type === "polygon") {
                const area = JSON.stringify({
                    coordinates: newFence[0]?.getLatLngs()[0],
                    type: type,
                } as AreaType);
                setGeofenceArea(area);
            }

            if (type === "polyline") {
                const area = JSON.stringify({
                    coordinates: newFence[0]?.getLatLngs(),
                    type: type,
                } as AreaType);
                setGeofenceArea(area);
            }

            if (type === "circle") {
                const area = JSON.stringify({
                    coordinates: newFence[0]?.getLatLng(),
                    type: type,
                    radius: newFence[0]?.getRadius(),
                } as AreaType);
                setGeofenceArea(area);
            }
        }
    }, []);

    const handleOnEditValues = useCallback((selectedGeofence: GeofenceType) => {
        if (selectedGeofence.name && !inInsert) {
            setGeofenceName(selectedGeofence.name);
            setGeofenceDescription(selectedGeofence.description ?? "");
            setGeofenceArea(selectedGeofence.area);
        }
    }, [
        inInsert,
        groupsList,
    ]);

    useEffect(() => {
        setFilteredList(getFilteredList(["name", "description"], geofencesList, query));
    }, [geofencesList, query]);

    const handleChange = useCallback((name: string, value: string) => {
        if (name === "name") {
            setGeofenceName(value);
        }
        if (name === "description") {
            setGeofenceDescription(value);
        }
        if (name === "group") {
            setGeofenceGroupName(value);
        }
    }, []);

    const getReport = useCallback(async (type: string) => {
        setLoading(true);

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        let dataBuffer = new ArrayBuffer(0);
        try {
            const { data } = await findAllGeofencesExportResponse(cookies.token,
                cookies.selectedParameters?.customer.id,
                cookies.selectedParameters?.department.id,
                geofencesList.length > 0 && geofencesList[0].activated
                    ? "activated"
                    : "deactivated", reportType, cookies.selectedParameters?.isViewingAll, isSimpleType)

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, cookies.selectedParameters?.customer.id, cookies.selectedParameters?.department.id, cookies.selectedParameters?.isViewingAll, geofencesList, handleClose]);

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 650,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 650, padding: 5 }}>
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton variant="rectangular" width={"100%"} height={350} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const renderGeofence = useMemo(() => {
        if (selectedGeofence?.name && geofenceArea) {
            const area = JSON.parse(geofenceArea) ?? null;
            if (area) {
                const coords = area?.coordinates;
                const type = area?.type;
                const radius = area?.radius ?? 0;

                if (type === "circle") {
                    return (
                        <Circle center={coords} radius={radius} color={geofenceColor} />
                    );
                } else if (type === "polygon") {
                    return <Polygon positions={coords} color={geofenceColor} />;
                } else if (type === "polyline") {
                    return <Polyline positions={coords} color={geofenceColor} />;
                }
            }
        }
    }, [geofenceColor, geofenceArea, selectedGeofence?.name]);

    const getMapCenter = useMemo(() => {
        if (selectedGeofence?.name && geofenceArea) {
            const area = JSON.parse(geofenceArea) ?? null;
            if (area) {
                const coords = area?.coordinates;
                const type = area?.type;

                if (type === "circle") {
                    return { lng: coords.lng, lat: coords.lat };
                } else if (type === "polygon") {
                    return { lng: coords[0].lng, lat: coords[0].lat };
                } else if (type === "polyline") {
                    return { lng: coords[0].lng, lat: coords[0].lat };
                }
            }
        } else return { lng: -56.087066, lat: -15.584697 };
    }, [geofenceArea, selectedGeofence?.name]);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                            "& .leaflet-container": { borderRadius: 3, border: "1px solid rgba(0, 63, 74, 0.12)" },
                        }}
                    >
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex" style={{ marginBottom: "10px" }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Identificação
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                >
                                    {selectedGeofence?.name && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedGeofence.activated ? "#00B700" : "#dc3545",
                                                    marginRight: 5,
                                                    width: "50%",
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={selectedGeofence.activated ? "Ativo" : "Inativo"}
                                            />
                                            {selectedGeofence.activated && <Chip
                                                style={{ color: "black", marginRight: 5, width: "50%" }}
                                                disabled={!location?.state?.canEdit || isImpersonationLocked()}
                                                onClick={() => {
                                                    setInEdit(true);
                                                }}
                                                label="Editar"
                                                icon={
                                                    <DriveFileRenameOutlineOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />}
                                            {false && <Chip
                                                style={{ color: "black", marginRight: 5, width: "50%" }}
                                                disabled={!location?.state?.canDelete}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeDelete(selectedGeofence.id);
                                                    }
                                                }}
                                                label="Excluir"
                                                icon={
                                                    <DeleteForeverOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />}

                                            <Chip
                                                style={{
                                                    color: "black",
                                                    marginRight: 5,
                                                    width: "50%",
                                                }}
                                                disabled={!location?.state?.canEdit || isImpersonationLocked()}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(selectedGeofence.id, selectedGeofence.activated ? "deactivate" : "activate")
                                                    }
                                                }}
                                                label={selectedGeofence.activated ? "Desativar" : "Ativar"}
                                                icon={
                                                    <RemoveCircleOutlineOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />

                                        </>
                                    )}
                                </Box>

                                {currentParameters?.isViewingAll && inInsert && <>
                                    <Box className="col-md-6" style={{ marginBottom: 10 }}>
                                        <Autocomplete
                                            id="chip-label-select-secretary"
                                            className="input-select-secretary"
                                            style={{
                                                width: "100%",
                                                minHeight: "56px",
                                                height: "56px",
                                                backgroundColor: "#ffffff29",
                                                borderColor: "lightgray",
                                            }}
                                            options={secretariesList}
                                            value={secretary}
                                            onChange={(_, option) => {
                                                setSecretary(option ?? secretaryDefault);
                                                setDepartment(departmentDefault);
                                            }}
                                            disabled={(inEdit || (!inEdit && !inInsert)) as boolean}
                                            getOptionLabel={(option) => `${option.reducedName}`}
                                            noOptionsText={"Nada encontrado"}
                                            renderInput={(params) => (
                                                <>
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex" }}
                                                    >
                                                        <InputLabel
                                                            id="country-label"
                                                            required
                                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                            shrink
                                                        >
                                                            Secretaria
                                                        </InputLabel>
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Selecione uma secretaria"
                                                            autoComplete="off"
                                                            required
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                notched: true,
                                                                label: "Secretaria",
                                                                classes: {
                                                                    notchedOutline: "custom-notch",
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </>
                                            )}
                                        />
                                    </Box>

                                    <Box className="col-md-6" style={{ marginBottom: 10 }}>
                                        <Autocomplete
                                            id="chip-label-select-department"
                                            className="input-select-department"
                                            style={{
                                                width: "100%",
                                                minHeight: "56px",
                                                height: "56px",
                                                backgroundColor: "#ffffff29",
                                                borderColor: "lightgray",
                                            }}
                                            options={departmentsList}
                                            value={department}
                                            onChange={(_, option) => {
                                                setDepartment(option ?? departmentDefault);
                                            }}
                                            disabled={(inEdit || (!inEdit && !inInsert)) as boolean}
                                            getOptionLabel={(option) => `${option.reducedName}`}
                                            noOptionsText={"Nada encontrado"}
                                            renderInput={(params) => (
                                                <>
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex" }}
                                                    >
                                                        <InputLabel
                                                            id="country-label"
                                                            required
                                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                            shrink
                                                        >
                                                            Departamento
                                                        </InputLabel>
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Selecione um departamento"
                                                            autoComplete="off"
                                                            required
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                notched: true,
                                                                label: "Departamento",
                                                                classes: {
                                                                    notchedOutline: "custom-notch",
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </>
                                            )}
                                        />
                                    </Box></>}

                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 15px 0px" }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-password-label"
                                            shrink
                                            required
                                            disabled={
                                                (selectedGeofence?.name &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            Nome
                                        </InputLabel>
                                        <TextField
                                            name="name"
                                            autoComplete="off"
                                            required
                                            InputProps={{
                                                label: "Nome",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={
                                                (selectedGeofence?.name &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            value={geofenceName}
                                            onChange={(e) => {
                                                handleChange(e.target.name, e.target.value);
                                            }}
                                            type="text"
                                            className="form-control form-control-login"
                                            placeholder="Digite o nome"
                                        />
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            className="visibility-password"
                                            disabled
                                            onMouseDown={(e: SyntheticEvent) => { }}
                                            edge="end"
                                            size="large"
                                        >
                                            <FenceOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>
                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 15px 0px" }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-password-label"
                                            shrink
                                            required
                                            disabled={
                                                (selectedGeofence?.name &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            Descrição
                                        </InputLabel>
                                        <TextField
                                            name="description"
                                            autoComplete="off"
                                            required
                                            InputProps={{
                                                label: "Descrição",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            value={geofenceDescription}
                                            onChange={(e) => {
                                                handleChange(e.target.name, e.target.value);
                                            }}
                                            disabled={
                                                (selectedGeofence?.name &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            type="text"
                                            className="form-control form-control-login"
                                            placeholder="Digite a descrição"
                                        />
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            className="visibility-password"
                                            disabled
                                            onMouseDown={(e: SyntheticEvent) => { }}
                                            edge="end"
                                            size="large"
                                        >
                                            <AbcOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>
                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 20px 0px" }}
                                >
                                    <Autocomplete
                                        id="chip-label-select-group"
                                        className="input-select-group"
                                        style={{
                                            width: "100%",
                                            minHeight: "56px",
                                            height: "56px",
                                            backgroundColor: "#ffffff29",
                                            borderColor: "lightgray",
                                        }}
                                        options={groupsList}
                                        value={groupsList.find(a => a.name === geofenceGroupName) ?? null}
                                        onChange={(_, option) => {
                                            setGeofenceGroupName(option?.name ?? "");
                                        }}
                                        getOptionLabel={(option) => `${option.name}`}
                                        noOptionsText={"Nada encontrado"}
                                        renderInput={(params) => (
                                            <>
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex" }}
                                                >
                                                    <InputLabel
                                                        id="group-label"
                                                        required
                                                        style={{ color: "gray", fontFamily: "Prometo" }}
                                                        shrink
                                                        disabled={
                                                            (selectedGeofence?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        Grupo
                                                    </InputLabel>
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Selecione um grupo"
                                                        autoComplete="off"
                                                        required
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            notched: true,
                                                            label: "Grupo",
                                                            classes: {
                                                                notchedOutline: "custom-notch",
                                                            },
                                                        }}
                                                    />
                                                </FormControl>
                                            </>
                                        )}
                                        disabled={
                                            (selectedGeofence?.name &&
                                                !inEdit &&
                                                !inInsert) as boolean
                                        }
                                    />
                                </Box>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Mapa
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                ></Box>

                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 5px 0px" }}
                                >
                                    <Box
                                        sx={{
                                            padding: 0,
                                            "& .leaflet-container": {
                                                position: "relative !important",
                                            },
                                        }}
                                    >
                                        <Autocomplete<AddressResult>
                                            id="geofence-address-search"
                                            className="search-place tw-mb-4 tw-w-full tw-min-w-0"
                                            options={addressResults}
                                            value={selectedAddress}
                                            inputValue={addressQuery}
                                            filterOptions={(options) => options}
                                            getOptionLabel={(option) => option.display_name}
                                            isOptionEqualToValue={(option, value) => option.display_name === value.display_name && option.lat === value.lat && option.lon === value.lon}
                                            loading={isSearchingAddress}
                                            loadingText="Buscando endereços..."
                                            noOptionsText={addressQuery.trim().length < 3 ? "Digite pelo menos 3 caracteres" : "Sem resultados"}
                                            clearText="Limpar endereço"
                                            openText="Mostrar endereços"
                                            closeText="Fechar sugestões"
                                            onInputChange={(_, value, reason) => {
                                                if (reason === "input" || reason === "clear") {
                                                    setSelectedAddress(null);
                                                    setAddressQuery(value);
                                                }
                                            }}
                                            onChange={(_, option) => {
                                                setSelectedAddress(option);
                                                setAddressQuery(option?.display_name ?? "");
                                                setIsSearchingAddress(false);
                                                if (option) {
                                                    geofenceMapRef.current?.flyTo({ lat: Number(option.lat), lng: Number(option.lon) }, 17);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    autoComplete="off"
                                                    label="Endereço"
                                                    placeholder="Digite um endereço ou local"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 56, backgroundColor: "background.paper" }, "& .MuiInputLabel-root": { fontFamily: "Prometo" } }}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isSearchingAddress && <CircularProgress color="inherit" size={18} aria-label="Buscando endereços" />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <MenuItem
                                                    {...props}
                                                    key={`${option.display_name}-${option.lat}-${option.lon}`}
                                                    component="li"
                                                    className="!tw-m-1.5 !tw-whitespace-normal !tw-break-words !tw-rounded-lg !tw-px-3 !tw-py-2.5 !tw-font-prometo hover:!tw-bg-emerald-50 dark:hover:!tw-bg-slate-700"
                                                >
                                                    {option.display_name}
                                                </MenuItem>
                                            )}
                                        />
                                        <MapContainer
                                            ref={geofenceMapRef}
                                            id="leaflet-container map-container"
                                            center={getMapCenter}
                                            style={{
                                                height: 350,
                                                width: "100%",
                                                transition: "all 0.5s ease-out allow-discrete",
                                            }}
                                            boxZoom={true}
                                            zoomControl={true}
                                            attributionControl
                                            zoomAnimation={true}
                                            zoom={25}
                                        >
                                            <TileLayer
                                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <FeatureGroup>
                                                <EditControl
                                                    position="topright"
                                                    onCreated={onCreateFence}
                                                    onEdited={onEditFence}
                                                    onDeleted={() => setGeofenceArea("")}
                                                    edit={{
                                                        allowIntersection: false,
                                                        edit: inEdit || inInsert,
                                                        remove: selectedGeofence?.name ? false : true,
                                                        toolbar: true,
                                                    }}
                                                    draw={{
                                                        rectangle: false,
                                                        circlemarker: false,
                                                        marker: false,
                                                        polygon: geofenceArea
                                                            ? false
                                                            : {
                                                                allowIntersection: false,
                                                                shapeOptions: {
                                                                    color: geofenceColor ?? "#003f4a",
                                                                },
                                                            },
                                                        circle: geofenceArea
                                                            ? false
                                                            : {
                                                                shapeOptions: {
                                                                    color: geofenceColor ?? "#003f4a",
                                                                },
                                                            },
                                                        polyline: geofenceArea
                                                            ? false
                                                            : {
                                                                allowIntersection: false,
                                                                shapeOptions: {
                                                                    color: geofenceColor ?? "#003f4a",
                                                                },
                                                            },
                                                    }}
                                                />
                                                {renderGeofence}
                                            </FeatureGroup>
                                        </MapContainer>
                                    </Box>
                                </Box>{" "}
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedGeofence);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        addressQuery,
        addressResults,
        selectedAddress,
        isSearchingAddress,
        currentParameters?.isViewingAll,
        department,
        departmentsList,
        secretariesList,
        secretary,
        getMapCenter,
        verifyBeforeUpdateStatus,
        location?.state?.canDelete,
        location?.state?.canEdit,
        verifyBeforeCancel,
        isToastVisible,
        validationText,
        verifyBeforeDelete,
        renderGeofence,
        selectedGeofence,
        geofenceArea,
        isOpenDetails,
        loading,
        groupsList,
        geofenceName,
        geofenceDescription,
        geofenceGroupName,
        handleChange,
        onSave,
        loadingEsqueleton,
        geofenceColor,
        onCreateFence,
        onEditFence,
        onUpdate,
        inInsert,
        inEdit,
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Cercas virtuais"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, (item: any) => {
                    setIsOpenDetails(true);
                    setSelectedGeofence(item);
                })}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome ou descrição"}
                onRefresh={getAllGeofences}
                onExport={getReport}
                disableAddButton={!location?.state?.canCreate}
                handleNext={(activated: string, page: number, size: number) => getAllGeofences("", activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item) => {
                    setIsOpenDetails(true);
                    setSelectedGeofence(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(94vw, 1100px)" },
    maxWidth: 1100,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default GeofencePage;
